import { ReactNode } from "react";
import styled from "styled-components";

import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";

import type { DataTableColumn } from "./index";
import { VisuallyHidden } from "./visuallyHidden";

const { color, radius, typeScale } = adminTokens;
const [, spaceSm, spaceMd, spaceLg] = adminTokens.spacing;

// The design's phone record card: 16px corners, padded 14px above and below and
// 16px at each side, its lines' labels in a 96px column (PHONE-R17, PHONE-R20).
const cardRadius = 16;
const cardPaddingY = 14;
const lineLabelWidth = 96;
const lineGap = 6;
const partGap = 10;
const headingFontSize = "15px";
const skeletonCardCount = 3;
const skeletonCardHeight = 120;

const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${spaceMd}px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Card = styled.li<{ $isSelected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${partGap}px;
  box-sizing: border-box;
  min-width: 0;
  padding: ${cardPaddingY}px ${spaceLg}px;
  border: 1px solid ${({ $isSelected }) => ($isSelected ? color.periwinkle : color.border)};
  border-radius: ${cardRadius}px;
  background-color: ${color.surface};
  color: ${color.navy};
  font-family: ${typeScale.body.fontFamily};
  font-size: ${typeScale.body.fontSize};
  line-height: ${typeScale.body.lineHeight};
`;

const SkeletonCard = styled.li`
  height: ${skeletonCardHeight}px;
  border: 1px solid ${color.border};
  border-radius: ${cardRadius}px;
  background-color: ${color.surface};
`;

const HeadingRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${partGap}px;
`;

const Heading = styled.div`
  flex: 1;
  min-width: 0;
  font-family: ${typeScale.labelValue.fontFamily};
  font-size: ${headingFontSize};
  line-height: 1.3;
  overflow-wrap: anywhere;
`;

const Badge = styled.div`
  flex: none;
`;

const Lines = styled.dl`
  display: flex;
  flex-direction: column;
  gap: ${lineGap}px;
  margin: 0;
`;

const Line = styled.div`
  display: grid;
  grid-template-columns: ${lineLabelWidth}px minmax(0, 1fr);
  gap: ${spaceMd}px;
  font-size: ${typeScale.meta.fontSize};
  line-height: 1.4;
`;

const LineLabel = styled.dt`
  color: ${color.inkSoft};
  font-family: ${typeScale.meta.fontFamily};
`;

const LineValue = styled.dd`
  margin: 0;
  overflow-wrap: anywhere;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spaceSm}px;

  & > *,
  & > * > * {
    flex: 1 1 auto;
  }
`;

const skeletonCards = Array.from({ length: skeletonCardCount }, (_, index) => index);

interface PhoneCardListProps<Row> {
  label: string;
  columns: DataTableColumn<Row>[];
  rows: Row[];
  getRowKey: (row: Row) => string;
  getRowTestId?: (row: Row) => string;
  renderRowDetail?: (row: Row) => ReactNode;
  selectedRowKey?: string;
  isLoading: boolean;
}

// A data table's rows on a phone: one card per row, headed by the first column, its
// badge beside the heading, the other columns as labelled lines and the actions
// at the bottom (PHONE-R16…R24). The first column is always the heading.
export const PhoneCardList = <Row,>({
  label,
  columns,
  rows,
  getRowKey,
  getRowTestId,
  renderRowDetail,
  selectedRowKey,
  isLoading,
}: PhoneCardListProps<Row>) => {
  const [headingColumn, ...otherColumns] = columns;
  if (!headingColumn) return null;

  const badgeColumns = otherColumns.filter((column) => column.phonePlacement === "badge");
  const lineColumns = otherColumns.filter(
    (column) => column.phonePlacement !== "badge" && !column.isHeaderHidden
  );
  const actionColumns = otherColumns.filter(
    (column) => column.phonePlacement !== "badge" && column.isHeaderHidden
  );

  return (
    <>
      <List aria-label={label} aria-busy={isLoading || undefined}>
        {isLoading
          ? skeletonCards.map((index) => <SkeletonCard key={index} />)
          : rows.map((row) => {
              const key = getRowKey(row);
              const isSelected = key === selectedRowKey;
              const detail = renderRowDetail?.(row);

              return (
                <Card
                  key={key}
                  data-testid={getRowTestId?.(row)}
                  aria-current={isSelected ? "true" : undefined}
                  $isSelected={isSelected}
                >
                  <HeadingRow>
                    <Heading data-testid="phone-card-heading">{headingColumn.render(row)}</Heading>
                    {badgeColumns.map((column) => (
                      <Badge key={column.id}>{column.render(row)}</Badge>
                    ))}
                  </HeadingRow>
                  {lineColumns.length > 0 && (
                    <Lines>
                      {lineColumns.map((column) => (
                        <Line key={column.id}>
                          <LineLabel>{column.header}</LineLabel>
                          <LineValue>{column.render(row)}</LineValue>
                        </Line>
                      ))}
                    </Lines>
                  )}
                  {detail}
                  {actionColumns.map((column) => (
                    <Actions key={column.id}>{column.render(row)}</Actions>
                  ))}
                </Card>
              );
            })}
      </List>
      {isLoading && <VisuallyHidden role="status">Loading rows</VisuallyHidden>}
    </>
  );
};
